// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack Context Help — Provider
// apps/web/src/components/engine/ContextHelpProvider.tsx
//
// React context that manages:
//  1. Enabled/disabled state (persisted in localStorage)
//  2. Help data cache (loaded once from API on first enable)
//  3. Lookup function for HelpBubble components
//
// Wraps the entire app in __root.tsx.
// ═══════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import {
  useContextHelpData,
  type ContextHelpItem,
} from '../../lib/api/context-help';

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'forgetrack-context-help-enabled';

function getStoredEnabled(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Default to false (off) — user must opt in
    return stored === 'true';
  } catch {
    return false;
  }
}

function setStoredEnabled(enabled: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    // localStorage not available — degrade gracefully
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface ContextHelpContextValue {
  /** Whether context help indicators are currently visible */
  enabled: boolean;
  /** Toggle context help on/off */
  setEnabled: (enabled: boolean) => void;
  /** Toggle context help */
  toggle: () => void;
  /** Look up help content by key */
  getHelp: (helpKey: string) => ContextHelpItem | undefined;
  /** Get related help items for a key */
  getRelated: (helpKey: string) => ContextHelpItem[];
  /** Whether help data is currently loading */
  loading: boolean;
  /** Whether help data has been loaded at least once */
  loaded: boolean;
}

const ContextHelpContext = createContext<ContextHelpContextValue>({
  enabled: false,
  setEnabled: () => {},
  toggle: () => {},
  getHelp: () => undefined,
  getRelated: () => [],
  loading: false,
  loaded: false,
});

// ─── Provider ────────────────────────────────────────────────────────────────

interface ContextHelpProviderProps {
  children: ReactNode;
  locale?: string;
}

export function ContextHelpProvider({
  children,
  locale = 'en',
}: ContextHelpProviderProps) {
  const [enabled, setEnabledState] = useState(getStoredEnabled);

  const setEnabled = useCallback((value: boolean) => {
    setEnabledState(value);
    setStoredEnabled(value);
  }, []);

  const toggle = useCallback(() => {
    setEnabledState(prev => {
      const next = !prev;
      setStoredEnabled(next);
      return next;
    });
  }, []);

  // Only fetch data when enabled (lazy loading)
  const { getHelp, getRelated, loading, loaded } = useContextHelpData(
    locale,
    enabled
  );

  const value = useMemo<ContextHelpContextValue>(
    () => ({ enabled, setEnabled, toggle, getHelp, getRelated, loading, loaded }),
    [enabled, setEnabled, toggle, getHelp, getRelated, loading, loaded]
  );

  return (
    <ContextHelpContext.Provider value={value}>
      {children}
    </ContextHelpContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useContextHelp() {
  return useContext(ContextHelpContext);
}

export default ContextHelpProvider;
